
import React, { useEffect } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { MedewerkerFormValues } from "./types";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { toast } from "sonner";

interface RoleFieldProps {
  form: UseFormReturn<MedewerkerFormValues>;
}

const RoleField = ({ form }: RoleFieldProps) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const userRole = user?.role || "medewerker";
  
  // Check if the current user can manage different types of roles
  const canManageAllRoles = userRole === "management";
  const canManagePlannerRoles = userRole === "management";
  const canManageMedewerkerRoles = hasPermission("medewerkers", "write");
  
  // Debug log for permission check
  useEffect(() => {
    console.log("RoleField - current user role:", userRole);
    console.log("RoleField - canManageAllRoles:", canManageAllRoles);
    console.log("RoleField - canManagePlannerRoles:", canManagePlannerRoles);
    console.log("RoleField - canManageMedewerkerRoles:", canManageMedewerkerRoles);
    console.log("RoleField - hasPermission for medewerkers.write:", hasPermission("medewerkers", "write"));
  }, [userRole, canManageAllRoles, canManagePlannerRoles, canManageMedewerkerRoles, hasPermission]);
  
  // Handle role changes with permission checks
  const handleRoleChange = (value: string) => {
    console.log("Role change requested to:", value);
    
    if (value === "management" && !canManageAllRoles) {
      toast.error("Alleen management kan deze rol toewijzen");
      return;
    }
    
    if (value === "planner" && !canManagePlannerRoles) {
      toast.error("Alleen management kan planner rollen toewijzen");
      return;
    }
    
    if (value === "medewerker" && !canManageMedewerkerRoles) {
      toast.error("Je hebt geen rechten om deze rol toe te wijzen");
      return;
    }
    
    // If the user has permission to manage this role, set it
    form.setValue("role", value as "medewerker" | "planner" | "management");
    console.log("Role successfully changed to:", value);
  };
  
  // If user can't manage any roles, disable the field
  const isFieldDisabled = !canManageMedewerkerRoles && !canManageAllRoles;
  
  // Only show roles that the user is permitted to manage
  const availableRoles = [];
  if (canManageMedewerkerRoles) availableRoles.push("medewerker");
  if (canManagePlannerRoles) availableRoles.push("planner");
  if (canManageAllRoles) availableRoles.push("management");
  
  // Log available roles
  useEffect(() => {
    console.log("Available roles for current user:", availableRoles);
  }, [availableRoles]);
  
  return (
    <FormField
      control={form.control}
      name="role"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Rol</FormLabel>
          <Select 
            onValueChange={handleRoleChange} 
            defaultValue={field.value}
            value={field.value}
            disabled={isFieldDisabled}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer een rol" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {availableRoles.includes("medewerker") && (
                <SelectItem value="medewerker">Medewerker</SelectItem>
              )}
              {availableRoles.includes("planner") && (
                <SelectItem value="planner">Planner</SelectItem>
              )}
              {availableRoles.includes("management") && (
                <SelectItem value="management">Management</SelectItem>
              )}
            </SelectContent>
          </Select>
          <FormDescription>
            Bepaalt welke functies de gebruiker kan gebruiken
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default RoleField;
